import { axios_instance } from "@/api/api";
import type { InfiniteData } from "@tanstack/vue-query";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, type Ref } from "vue";
import { toast } from "vue3-toastify";
import type { browser_history } from ".";

type BrowserHistoryResponse = {
  data: browser_history[] | null;
  total_filter: number;
};

type BrowserHistoryFilters = {
  action_id: string;
  keyword?: string;
  created_from?: string;
  created_to?: string;
  size: number;
};

type BrowserHistoryQueryKey = ["browser_history", BrowserHistoryFilters];

const fetchBrowserHistory = async ({
  queryKey,
  pageParam = 1,
}: {
  queryKey: BrowserHistoryQueryKey;
  pageParam?: number;
}): Promise<BrowserHistoryResponse> => {
  const [, filters] = queryKey;

  const createdFrom = filters.created_from
    ? Date.parse(filters.created_from)
    : undefined;
  if (filters.created_from && Number.isNaN(createdFrom)) {
    toast.error("Ngày bắt đầu không hợp lệ");
    return { data: [], total_filter: 0 };
  }

  const createdTo = filters.created_to
    ? Date.parse(filters.created_to)
    : undefined;
  if (filters.created_to && Number.isNaN(createdTo)) {
    toast.error("Ngày kết thúc không hợp lệ");
    return { data: [], total_filter: 0 };
  }

  const payload = {
    action_id: filters.action_id,
    page: pageParam,
    size: filters.size,
    created_from: createdFrom,
    created_to: createdTo,
    keyword: filters.keyword,
  };

  const response = await axios_instance.post<BrowserHistoryResponse>(
    "/api/v1/view/browser-history/pagination",
    payload,
  );

  return response.data;
};

export function useBrowserHistoryInfiniteQuery(
  filters: Ref<BrowserHistoryFilters>,
) {
  const queryKey = computed<BrowserHistoryQueryKey>(() => [
    "browser_history",
    filters.value,
  ]);

  const queryResult = useInfiniteQuery<
    BrowserHistoryResponse,
    Error,
    InfiniteData<BrowserHistoryResponse>,
    BrowserHistoryQueryKey,
    number
  >({
    queryKey,
    queryFn: fetchBrowserHistory,
    initialPageParam: 1,
    enabled: computed(() => Boolean(filters.value.action_id)),
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = filters.value.size || 24;
      const totalPages = Math.ceil(lastPage.total_filter / pageSize);
      return allPages.length < totalPages ? allPages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

  const browserHistories = computed(() => {
    return (
      queryResult.data.value?.pages.flatMap((page) => page.data ?? []) ?? []
    );
  });

  const totalFiltered = computed(() => {
    return queryResult.data.value?.pages[0]?.total_filter ?? 0;
  });

  return {
    ...queryResult,
    browserHistories,
    totalFiltered,
  };
}
